import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import GlobalStyles from './styles/GlobalStyles';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import WelcomeScreen from './components/WelcomeScreen/WelcomeScreen';
import CoachDashboard from './components/CoachDashboard/CoachDashboard';
import JumperDashboard from './components/JumperDashboard/JumperDashboard';
import SignIn from './components/Auth/SignIn';
import SignUp from './components/Auth/SignUp';
import SignUpComplete from './components/Auth/SignUpComplete';
import GoogleCompleteSignup from './components/Auth/GoogleCompleteSignup';
import ProtectedRoute from './components/Auth/ProtectedRoute';
import LoadingSpinner from './components/Common/LoadingSpinner';
import HowToStart from './components/HowToStart/HowToStart';
import Pricing from './components/Pricing/Pricing';
import PrivacyPolicy from './components/PrivacyPolicy/PrivacyPolicy';
import TermsOfService from './components/TermsOfService/TermsOfService';
import CookieConsent from './components/CookieConsent/CookieConsent';
import SubscriptionSuccess from './components/SubscriptionSuccess/SubscriptionSuccess';
import DevDashboard from './components/DevDashboard/DevDashboard';
import DevOverview from './components/DevDashboard/DevOverview';
import DevUsers from './components/DevDashboard/DevUsers';
import DevCoaches from './components/DevDashboard/DevCoaches';
import DevAthletes from './components/DevDashboard/DevAthletes';
import DevSubscriptions from './components/DevDashboard/DevSubscriptions';

function isDevHost() {
  if (typeof window === 'undefined') return false;
  const h = window.location.hostname;
  return h === 'dev.sjmate.com' || h === 'localhost';
}

function DevRoute() {
  if (!isDevHost()) return <Navigate to="/" replace />;
  return (
    <ProtectedRoute>
      <DevDashboard />
    </ProtectedRoute>
  );
}

function ScrollToTop() {
  const location = useLocation();
  
  useEffect(() => {
    // Scroll to top on every route change so user always lands at top of page
    window.scrollTo(0, 0);
    // Ensure scrolling is always enabled on route change
    document.body.style.overflow = '';
    document.body.style.position = '';
    document.body.style.top = '';
    document.body.style.width = '';
  }, [location.pathname]);
  
  return null;
}

function AppRoutes() {
  const { isLoading } = useAuth();

  if (isLoading) {
    return <LoadingSpinner />;
  }

  return (
    <>
      <ScrollToTop />
      <CookieConsent />
      <Routes>
        <Route path="/signin" element={<SignIn />} />
      <Route path="/signup" element={<SignUp />} />
      <Route path="/signup/complete" element={<SignUpComplete />} />
      <Route path="/auth/google/callback" element={<div>Completing sign in...</div>} />
      <Route path="/auth/google/complete" element={<GoogleCompleteSignup />} />
      <Route path="/" element={<WelcomeScreen />} />
      <Route path="/how-to-start" element={<HowToStart />} />
      <Route path="/pricing" element={<Pricing />} />
      <Route path="/subscription-success" element={<SubscriptionSuccess />} />
      <Route path="/privacy-policy" element={<PrivacyPolicy />} />
      <Route path="/terms-of-service" element={<TermsOfService />} />
      <Route path="/jumper" element={<Navigate to="/jumper/home" replace />} />
      <Route 
        path="/jumper/*" 
        element={
          <ProtectedRoute requiredRole="athlete">
            <JumperDashboard />
          </ProtectedRoute>
        } 
      />
      <Route path="/coach" element={<Navigate to="/coach/home" replace />} />
      <Route 
        path="/coach/*" 
        element={
          <ProtectedRoute requiredRole="coach">
            <CoachDashboard />
          </ProtectedRoute>
        } 
      />
      <Route path="/dev/" element={<Navigate to="/dev" replace />} />
      <Route path="/dev" element={<DevRoute />}>
        <Route index element={<DevOverview />} />
        <Route path="users" element={<DevUsers />} />
        <Route path="coaches" element={<DevCoaches />} />
        <Route path="athletes" element={<DevAthletes />} />
        <Route path="subscriptions" element={<DevSubscriptions />} />
      </Route>
      </Routes>
    </>
  );
}

function App() {
  return (
    <AuthProvider>
      <Router>
        <GlobalStyles />
        <AppRoutes />
      </Router>
    </AuthProvider>
  );
}

export default App;
